import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { API_PATH } from 'src/environments/environment'; // Importar API_PATH
import { getBriefingTypeDescriptionByValue } from 'src/app/domain/registration/enums/briefing-type.enum'; // Importar a função do enum

// Interface para criar/atualizar metadados
export interface IBriefingMetadata {
    id?: string;
    description: string;
    type: number; 
    customerId: string;
    partnerId: string;
    systemId: string;
    logoUrl?: string; // Mantido por enquanto
}

export interface IBriefingDisplay { // Para List/Get
    id: string;
    code: number;
    description: string;
    type: number;
    typeDescription?: string; 
    customerId: string;
    customerName?: string;
    partnerId: string;
    partnerName?: string;
    systemId: string;
    systemName?: string;
    createdAt: Date;
    updatedAt: Date;
    customer?: { id: string, name?: string };
    partner?: { id: string, name?: string , partnerCommission?: string};
    system?: { id: string, name?: string, image?: string };
    mandatoryPlan: boolean;
    createdBy:string;
}

// NOVA Interface para exibir uma RESPOSTA de Briefing na lista
export interface IBriefingResponseDisplay {
    id: string; // ID da Resposta
    answers: Record<string, any>;
    fileUrls?: Record<string, string[]> | null;
    submittedAt: Date;
    briefingId: string;
    // Dados aninhados da definição do briefing
    briefing?: {
        id: string;
        code: number;
        description: string;
        type: number;
        typeDescription?: string;
        customerId: string;
        customerName?: string;
        partnerId: string;
        partnerName?: string;
        systemId: string;
        systemName?: string;
        createdAt: Date;
        updatedAt: Date;
        customer?: { id: string, name?: string };
        partner?: { id: string, name?: string };
        system?: { id: string, name?: string, image?: string };
        mandatoryPlan: boolean;
        createdBy:string;
    }
}

@Injectable({
    providedIn: 'root'
})
export class BriefingsService {
    // Definir apiUrl diretamente
    private apiUrl = `${API_PATH}briefings`;

    // Manter títulos se forem usados em algum lugar
    title = 'Briefing';
    pluralTitle = 'Briefings';

    constructor(
        protected httpClient: HttpClient, 
    ) { }

    listAll(filter: any, usertype: string,partenerId:any): Observable<[IBriefingDisplay[], number]> {
        let params = new HttpParams();

        // Adiciona os filtros existentes
        Object.keys(filter).forEach(key => {
            if (key !== 'answered' && filter[key] !== null && filter[key] !== undefined && filter[key] !== '') {
                params = params.set(key, filter[key]);
            }
        });

        // Adiciona o novo parâmetro 'usertype'
        if (usertype !== null && usertype !== undefined && usertype !== '') {
            params = params.set('usertype', usertype);
        }
        // Adiciona o novo parâmetro 'partnersId'
        if (partenerId !== null && partenerId !== undefined && partenerId !== '') {
            params = params.set('partenerId', partenerId);
        }

        // Chama GET /briefings
        return this.httpClient.get<[any[], number]>(this.apiUrl, { params })
            .pipe(
                map(([data, total]) => {
                    const mappedData = data.map(item => this.mapBriefingToDisplay(item));
                    return [mappedData, total];
                })
            );
    }

    // NOVO Método para listar RESPOSTAS
    listAllResponses(filter: any,usertype: string,partenerId:any): Observable<[IBriefingResponseDisplay[], number]> { 
        let params = new HttpParams();
        Object.keys(filter).forEach(key => {
            // Garantir que não passa 'answered' para este endpoint
            if (key !== 'answered' && filter[key] !== null && filter[key] !== undefined && filter[key] !== '') {
                params = params.set(key, filter[key]);
            }
        });
                // Adiciona o novo parâmetro 'usertype'
        if (usertype !== null && usertype !== undefined && usertype !== '') {
            params = params.set('usertype', usertype);
        }
        // Adiciona o novo parâmetro 'partnersId'
        if (partenerId !== null && partenerId !== undefined && partenerId !== '') {
            params = params.set('partenerId', partenerId);
        }
        // Chama GET /briefings/responses
        return this.httpClient.get<[any[], number]>(`${this.apiUrl}/responses`, { params })
            .pipe(
                map(([data, total]) => {
                    const mappedData = data.map(item => this.mapResponseToDisplay(item));
                    return [mappedData, total];
                })
            );
    }

    // Get para buscar DEFINIÇÃO
    get(id: string): Observable<IBriefingDisplay> {
        // Chama GET /briefings/:id
        return this.httpClient.get<any>(`${this.apiUrl}/${id}`).pipe(
            map(item => this.mapBriefingToDisplay(item)) 
        );
    }
    
    // Get para buscar UMA RESPOSTA
    getResponse(responseId: string): Observable<IBriefingResponseDisplay> {
         // Chama GET /briefings/responses/:responseId
        return this.httpClient.get<any>(`${this.apiUrl}/responses/${responseId}`).pipe(
            map(item => this.mapResponseToDisplay(item))
        );
    }

    // Criar/Atualizar DEFINIÇÃO (sem alterações)
    create(data: IBriefingMetadata): Observable<IBriefingDisplay> {
         return this.httpClient.post<any>(this.apiUrl, data)
             .pipe(map(item => this.mapBriefingToDisplay(item)));
    }
    updateMetadata(id: string, data: Partial<IBriefingMetadata>): Observable<IBriefingDisplay> {
         return this.httpClient.put<any>(`${this.apiUrl}/${id}`, data)
              .pipe(map(item => this.mapBriefingToDisplay(item)));
    }

    updateRegisteredStatus(id: string): Observable<any> { 
        return this.httpClient.patch<any>(`${this.apiUrl}/${id}/register`,{});
    }

    // Submeter Respostas (retorno pode precisar de ajuste se API mudou, mas por enquanto ok)
    submitAnswers(id: string, answers: Record<string, any>): Observable<any> { // Usar any ou criar interface para BriefingResponse base
        const payload = { answers };
        return this.httpClient.patch<any>(`${this.apiUrl}/${id}/answers`, payload);
           // .pipe(map(item => this.mapResponseToDisplay(item))); // Mapear se quiser usar IBriefingResponseDisplay
    }
    submitAnswersWithFiles(id: string, formData: FormData): Observable<any> { // Usar any ou criar interface para BriefingResponse base
        return this.httpClient.patch<any>(`${this.apiUrl}/${id}/answers`, formData);
           // .pipe(map(item => this.mapResponseToDisplay(item))); // Mapear se quiser usar IBriefingResponseDisplay
    }

    // Nova rota que usa AnyFilesInterceptor para maior flexibilidade com campos de arquivo
    submitAnswersAnyFiles(id: string, formData: FormData): Observable<any> {
        return this.httpClient.post<any>(`${this.apiUrl}/${id}/answers/any`, formData);
    }

    // Delete DEFINIÇÃO (e respostas associadas via cascade)
    delete(id: string): Observable<void> {
        return this.httpClient.delete<void>(`${this.apiUrl}/${id}`);
    }

    // NOVO: Delete RESPOSTA específica
    deleteResponse(responseId: string): Observable<void> {
        return this.httpClient.delete<void>(`${this.apiUrl}/responses/${responseId}`);
    }

    // Método auxiliar de mapeamento para DEFINIÇÃO
    private mapBriefingToDisplay(item: any): IBriefingDisplay {
        if (!item) return item;
        const displayItem: IBriefingDisplay = {
            id: item.id,
            code: item.code,
            description: item.description,
            customerId: item.customerId,
            customerName: item.customer?.companyName || item.customer?.name || ' ',
            partnerId: item.partnerId,
            partnerName: item.partner?.name || item.partner?.companyName || ' ',
            systemId: item.systemId,
            systemName: item.system?.name || ' ',
            createdAt: new Date(item.createdAt),
            updatedAt: new Date(item.updatedAt),
            type: item.type,
            typeDescription: getBriefingTypeDescriptionByValue(item.type),
            customer: item.customer,
            partner: item.partner,
            system: item.system,
            mandatoryPlan: item.mandatoryPlan,
            createdBy:item.createdBy,
        };
        return displayItem;
    }

    // NOVO: Método auxiliar de mapeamento para RESPOSTA
    private mapResponseToDisplay(item: any): IBriefingResponseDisplay {
        if (!item) return item;
        const displayResponse: IBriefingResponseDisplay = {
            id: item.id,
            answers: item.answers || {},
            fileUrls: item.fileUrls,
            submittedAt: new Date(item.submittedAt),
            briefingId: item.briefingId,
            briefing: item.briefing ? {
                ...this.mapBriefingToDisplay(item.briefing), // Reutiliza mapeamento da definição
            } : undefined
        };
         // Adicionar campos de acesso rápido para a tabela (opcional, mas facilita)
         // Ajustar para usar companyName se a tabela for configurada assim
        (displayResponse as any)['briefing.code'] = displayResponse.briefing?.code;
        (displayResponse as any)['briefing.description'] = displayResponse.briefing?.description;
        (displayResponse as any)['briefing.customerName'] = displayResponse.briefing?.customerName; // <-- Chave corrigida para briefing.customerName
        (displayResponse as any)['briefing.typeDescription'] = displayResponse.briefing?.typeDescription;

        return displayResponse;
    }
} 